import { deleteRegisterSupportOrganizaion, useRegisterSupportOrganization } from "@/api/services/admin/rso";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Tag } from "@/components/elements/Tag";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { RsoStatusTag } from "@/features/rso/components/RsoStatusTag";
import { RsoSummary } from "@/features/rso/components/RsoSummary";
import { RsoUserTable } from "@/features/rso/components/RsoUserTable";
import { useDialog } from "@/hooks/useDialog";
import { AppRoutes } from "@/routes/config";
import { black } from "@/theme/colors";
import { ERSOContractStatus, USER_TYPE } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { Link, useNavigate } from "react-router-dom";

export const RsoDetailPage: FC = () => {
  const { data: rsoResult, isLoading } = useRegisterSupportOrganization();
  const rsoData = rsoResult?.data;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirmDeleteDialog = useDialog();

  const rsoUsers = rsoData?.users ? rsoData.users.filter((user) => user.type == USER_TYPE.RSO_USER) : [];

  if (isLoading || !rsoData) return <LoadingPage />;

  const handleDeleteRso = async () => {
    try {
      if (!rsoData?.id) {
        return;
      }

      const agreeToDelete = await confirmDeleteDialog.show();
      if (!agreeToDelete) {
        return;
      }

      const rs = await deleteRegisterSupportOrganizaion(String(rsoData.id));
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(AppRoutes.rsoList);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_LIST] });
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Stack gap={3}>
      <TopPage
        enablePageBack={false}
        title={
          <Typography variant="head32Bold" display="flex" alignItems="flex-end" gap={2}>
            {rsoData.rsoName}
            <Typography component="span" variant="sub16Semi">
              ID: {rsoData.rsoRegistrationNumber ?? "----"}
            </Typography>
            <Typography component="span" variant="sub16Semi">
              最終ログイン日時: {rsoData.lastLoginAt ? dayjs(rsoData.lastLoginAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE_TIME}
            </Typography>
          </Typography>
        }
        rightContent={
          <Stack direction="row" alignItems="center" gap={3}>
            <RsoStatusTag status={rsoData.contractStatus} />
            {rsoData.deletedAt && <Tag enablePoint variant="bold" title="削除済み" color={black[95]} pointColor={black[70]} />}
            {(rsoData.contractStatus == ERSOContractStatus.CANCELED_CONTRACT || rsoData.contractStatus == ERSOContractStatus.TRIAL_CANCELLED) &&
              !rsoData.deletedAt && (
                <Button variant="danger" onClick={handleDeleteRso}>
                  削除
                </Button>
              )}
          </Stack>
        }
      />
      <RsoSummary />
      <RsoUserTable users={rsoUsers} />
      <ConfirmDeleteModal
        open={confirmDeleteDialog.open}
        onClose={confirmDeleteDialog.cancel}
        onOk={confirmDeleteDialog.confirm}
        message="削除してよろしいですか？"
      />
      <FooterActionBar mt={2}>
        <Stack width="100%" justifyContent="space-between" flexDirection="row">
          <Button variant="ghost" size="sm" component={Link} to={AppRoutes.rsoList}>
            戻る
          </Button>
          {!rsoData.deletedAt && (
            <Button size="sm" component={Link} to={getDynamicRoute(AppRoutes.rsoEdit, { id: rsoData.id })}>
              編集
            </Button>
          )}
        </Stack>
      </FooterActionBar>
    </Stack>
  );
};
